'use client';

/**
 * An Admin dashboard page starts the same way: where it sits (menu › section),
 * its icon and name, the line the menu shows under it, then the page's own
 * actions. The words come from the menu (src/lib/staff-tools/admin-menu.ts), so
 * the menu and the page never disagree.
 * The page body sits in one width and padding.
 */
import { Icon } from '@iconify/react';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';

import { adminPageOf } from '@/lib/staff-tools/admin-menu';

const WIDTH = { narrow: 'max-w-4xl', normal: 'max-w-6xl', wide: 'max-w-7xl' } as const;

export function StaffPage({
    title,
    description,
    actions,
    badges,
    below,
    width = 'normal',
    children,
}: {
    /** A detail page's own name (a project, a panelist); a list page takes the menu's. */
    title?: ReactNode;
    /** More than the menu's line, when the page needs it. */
    description?: ReactNode;
    actions?: ReactNode;
    /** Status chips beside the title. */
    badges?: ReactNode;
    /** Anything the page shows under its title (a detail page's ids and dates). */
    below?: ReactNode;
    width?: keyof typeof WIDTH;
    children: ReactNode;
}) {
    const pathname = usePathname() ?? '';
    const place = adminPageOf(pathname);
    const isList = place && pathname === place.item.href;
    return (
        <div className={`flex w-full ${WIDTH[width]} flex-col gap-6 px-6 pb-10`}>
            <header className="flex flex-col gap-2">
                {place ? (
                    <nav aria-label="Breadcrumb" className="text-tiny text-foreground-500">
                        <ol className="flex flex-wrap items-center gap-1">
                            <li>{place.menu.label}</li>
                            {place.section.title !== place.menu.label ? (
                                <>
                                    <li aria-hidden>›</li>
                                    <li>{place.section.title}</li>
                                </>
                            ) : null}
                            {!isList ? (
                                <>
                                    <li aria-hidden>›</li>
                                    <li>
                                        <a href={place.item.href} className="hover:text-primary">
                                            {place.item.label}
                                        </a>
                                    </li>
                                </>
                            ) : null}
                        </ol>
                    </nav>
                ) : null}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                        {place ? (
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-medium bg-primary-50 text-primary">
                                <Icon icon={place.item.icon} aria-hidden className="h-5 w-5" />
                            </span>
                        ) : null}
                        <h1 className="truncate text-xlarge font-semibold">
                            {title ?? place?.item.label}
                        </h1>
                        {badges}
                    </div>
                    {actions ? (
                        <div className="flex flex-wrap items-center gap-2">{actions}</div>
                    ) : null}
                </div>
                {description !== undefined || (isList && place) ? (
                    <p className="max-w-3xl text-small text-foreground-600">
                        {description ?? place?.item.description}
                    </p>
                ) : null}
                {below}
            </header>
            {children}
        </div>
    );
}
